import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { SplitButton } from 'primereact/splitbutton';
import { growlErrors, growlActionSuccess } from '../../../../../utils/NotificationUtil';
import {
  sendSurveyProjectEmailReminders,
  sendSurveyProjectEmailInvitations,
} from '../../../../../services/apps/SurveyService';
import { AccountContext } from '../../../../../components/AccountContext';
import { NotificationContext } from '../../../../../components/GrowlNotifications';
import { buildMessages, getRespondentStatuses } from '../../../../../services/apps/SurveyProjectHelper';

const SurveyProjectEmailButton = ({ surveyProject }) => {
  const [isSending, setIsSending] = useState(false);

  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const messages = buildMessages();

  const sendReminder = async () => {
    const surveyProjectId = surveyProject.id;
    const input = {
      accountId: currentAccount,
      surveyProjectId,
    };

    setIsSending(true);

    try {
      await sendSurveyProjectEmailReminders(input);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
      setIsSending(false);
    } catch (errors) {
      growlErrors(growl, errors);
      setIsSending(false);
    }
  };

  const sendInvite = async () => {
    const surveyProjectId = surveyProject.id;
    const input = {
      accountId: currentAccount,
      surveyProjectId,
    };

    setIsSending(true);

    try {
      await sendSurveyProjectEmailInvitations(input);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    } finally {
      setIsSending(false);
    }
  };

  if (!surveyProject) return null;

  const { surveys } = surveyProject;

  const respondentStatuses = getRespondentStatuses(surveys);

  const onlyInvitesCanBeSent =
    respondentStatuses.includes('NEW') &&
    !respondentStatuses.includes('INVITED') &&
    !respondentStatuses.includes('STARTED');

  const onlyRemindersCanBeSent =
    (respondentStatuses.includes('INVITED') || respondentStatuses.includes('STARTED')) &&
    !respondentStatuses.includes('NEW');

  const invitesAndRemindersCanBeSent =
    respondentStatuses.includes('NEW') &&
    (respondentStatuses.includes('INVITED') || respondentStatuses.includes('STARTED'));

  if (onlyInvitesCanBeSent) {
    return (
      <Button
        label={formatMessage(messages.sendInvite)}
        icon="pi pi-envelope"
        onClick={sendInvite}
        disabled={isSending}
      />
    );
  }

  if (onlyRemindersCanBeSent) {
    return (
      <Button
        label={formatMessage(messages.sendRemind)}
        icon="pi pi-envelope"
        onClick={sendReminder}
        disabled={isSending}
      />
    );
  }

  if (invitesAndRemindersCanBeSent) {
    const menu = [
      {
        label: formatMessage(messages.sendInvite),
        command: () => {
          sendInvite();
        },
      },
      {
        label: formatMessage(messages.sendRemind),
        command: () => {
          sendReminder();
        },
      },
    ];

    return (
      <SplitButton label={formatMessage(messages.sendInvite)} icon="pi pi-envelope" model={menu} disabled={isSending} />
    );
  }
};

SurveyProjectEmailButton.propTypes = {
  surveyProject: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default SurveyProjectEmailButton;
